/* istanbul ignore file */
import {
  createClient,
  fetchExchange,
  ssrExchange,
  Provider,
  cacheExchange as documentCacheExchange,
} from 'urql';
import { cacheExchange } from '@urql/exchange-graphcache';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { keys } from './cacheKeySettings';
import { addressBookCacheUpdates } from './addressBookCacheUpdates';
import { preparedBasketCacheUpdates } from './preparedBasketCacheUpdates';
import { createGqlApiUrl } from './createGqlApiUrl';

export function createUrqlClient(
  initialState?: NonNullable<Parameters<typeof ssrExchange>[0]>['initialState'],
) {
  const ssrExCache = ssrExchange({
    isClient: typeof window !== 'undefined',
    initialState,
  });

  return {
    ssrExCache,
    client: createClient({
      url: createGqlApiUrl(),
      exchanges: [
        cacheExchange({
          // TODO: add schema awareness after merging endpoints and the introspection is correct
          // schema: schema as CacheExchangeOpts['schema'],
          keys,
          updates: {
            Mutation: {
              ...addressBookCacheUpdates,
              ...preparedBasketCacheUpdates,
            },
          },
          // NOTE uncomment this if you are having inexplicable `null` values from `useQuery`, and want to find out why
          // logger: (...args) => console.log(...args);
        }),
        ssrExCache,
        fetchExchange,
      ],
      suspense: true,
    }),
  };
}

export const createUrqlServerClient = () => {
  const ssrCache = ssrExchange({
    isClient: false,
    initialState: {},
  });

  return {
    ssrCache,
    ssrClient: createClient({
      url: createGqlApiUrl(true),
      exchanges: [documentCacheExchange, ssrCache, fetchExchange],
      suspense: false,
      requestPolicy: 'network-only',
    }),
  };
};

export const UrqlProvider = ({ children, pageProps }: { children: ReactNode; pageProps: any }) => {
  const [restoredUrqlState, setRestoredUrqlState] = useState(pageProps.urqlState);

  const { ssrExCache, client: urqlClient } = useMemo(() => {
    const client = createUrqlClient(pageProps.urqlState);
    // Serialize the urqlClient to null on the client-side.
    // This ensures we don't share client and server instances of the urqlClient.
    (client.client as any).toJSON = () => null;

    return client;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // restore server fetched data on client page transition
  if (pageProps.urqlState !== restoredUrqlState) {
    setRestoredUrqlState(pageProps.urqlState);
    if (pageProps.urqlState) {
      ssrExCache.restoreData(pageProps.urqlState);
    }
  }

  return <Provider value={urqlClient}>{children}</Provider>;
};
